import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import { useSendFlow } from "../../Context/SendFlowContext";
import { fetchContractsPaginated } from "../../api/envelope";
import type { ContractSummary } from "../../api/envelope";

import "../../Styles/DashboardPageStyles/Dashboard.css";
import Sidebar from "../Partials/Sidebar";
import TrialBanner from "../Partials/TrialBanner";
import EmptyState from "../Partials/EmptyState";
import ProfileDropdown from "../Partials/ProfileDropdown";
import { DocumentsIllustration } from "../Partials/EmptyStateIllustrations";

function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{
    name: string;
    email: string;
    loginMethod: string;
  } | null>(null);

  const { user, loading, fetchUserProfile } = useUser();
  const { setStartDocFile } = useSendFlow();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelected = (file: File) => {
    setStartDocFile(file);
    navigate("/send");
  };

  // Real data state
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const strayBackdrops = document.querySelectorAll<HTMLElement>(
      ".picker-backdrop.is-open, .picker-backdrop[aria-hidden='false']"
    );

    strayBackdrops.forEach((el) => {
      el.classList.remove("is-open");
      el.setAttribute("aria-hidden", "true");
    });
  }, []);

  /**
   * Once the context finishes loading, decide what to do.
   * - If no user -> redirect to /login.
   * - If user exists -> fetch profile (only once).
   */
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login", { replace: true });
      } else {
        (async () => {
          const data = await fetchUserProfile();
          if (data) setProfile(data);
        })();
      }
    }
  }, [user, loading, navigate, fetchUserProfile]);

  // Fetch real contracts on mount (once user is available)
  const fetchIdRef = useRef(0);
  const loadContracts = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setDataLoading(true);
      const res = await fetchContractsPaginated({
        page: 1,
        limit: 5,
        sort: "newest",
      });
      if (id !== fetchIdRef.current) return; // stale (strict mode double-fire)
      setContracts(res.contracts);
      setTotalDocs(res.total);
      setStatusCounts(res.statusCounts ?? {});
    } catch (err) {
      if (id !== fetchIdRef.current) return;
      console.error("Dashboard: failed to load contracts", err);
      setContracts([]);
      setTotalDocs(0);
    } finally {
      if (id === fetchIdRef.current) setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && user) {
      loadContracts();
    }
  }, [loading, user, loadContracts]);

  // Use server-provided status counts for accurate stats across ALL documents
  const pendingCount =
    (statusCounts["sent"] ?? 0) + (statusCounts["partially_signed"] ?? 0);
  const completedCount = statusCounts["completed"] ?? 0;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusPillClass = (computedStatus: string) => {
    const s = computedStatus.toLowerCase().replace(/[\s_]+/g, "-");
    switch (s) {
      case "sent":
        return "status-pill--sent";
      case "partially-signed":
        return "status-pill--partially-signed";
      case "completed":
        return "status-pill--completed";
      case "voided":
        return "status-pill--voided";
      case "expired":
        return "status-pill--expired";
      default:
        return "status-pill--sent";
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading-card">
          <span className="dashboard-loading-dot" />
          <span className="dashboard-loading-dot" />
          <span className="dashboard-loading-dot" />
        </div>
      </div>
    );
  }

  if (!user) {
    // If somehow rendered before redirect, render nothing
    return null;
  }

  /** Ghost preview table for the empty state */
  const ghostPreview = (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Ghost header */}
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "10px 14px",
          background: "#f9fafb",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            width: 120,
            height: 10,
            borderRadius: 5,
            background: "#e2e8f0",
          }}
        />
        <div
          style={{
            width: 60,
            height: 10,
            borderRadius: 5,
            background: "#e2e8f0",
          }}
        />
        <div
          style={{
            width: 80,
            height: 10,
            borderRadius: 5,
            background: "#e2e8f0",
          }}
        />
        <div
          style={{
            width: 80,
            height: 10,
            borderRadius: 5,
            background: "#e2e8f0",
          }}
        />
      </div>
      {/* Ghost rows */}
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 12,
            padding: "12px 14px",
            borderBottom: i < 3 ? "1px solid #f1f5f9" : "none",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 100 + i * 10,
              height: 10,
              borderRadius: 5,
              background: "#f1f5f9",
            }}
          />
          <div
            style={{
              width: 50,
              height: 18,
              borderRadius: 999,
              background: "#f1f5f9",
            }}
          />
          <div
            style={{
              width: 70,
              height: 10,
              borderRadius: 5,
              background: "#f1f5f9",
            }}
          />
          <div
            style={{
              width: 70,
              height: 10,
              borderRadius: 5,
              background: "#f1f5f9",
            }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="dashboard">
      <Sidebar active="Dashboard" />

      <div className="dashboard-main">
        <TrialBanner />
        {/* TOP ROW: title + user pill */}
        <div className="dashboard-header">
          <div className="dashboard-header-left">
            <h2 className="dashboard-title">
              Welcome back, {profile?.name || "User"}!
            </h2>
            <p className="dashboard-subtitle">
              Here's a quick snapshot of your documents and signatures.
            </p>
          </div>

          <ProfileDropdown
            name={profile?.name || ""}
            email={profile?.email || ""}
          />
        </div>

        {/* STATS CARDS */}
        <div className="dashboard-stats">
          <div className="dashboard-stat">
            <div className="dashboard-stat-value">
              {dataLoading ? <span style={{ display: 'inline-block', width: 40, height: 20, borderRadius: 4, background: '#e5e7eb', animation: 'pulse 1.5s ease-in-out infinite' }} /> :totalDocs}
            </div>
            <span className="dashboard-stat-label">Total documents</span>
            <span className="dashboard-stat-sub">
              All agreements you've created or received.
            </span>
          </div>
          <div className="dashboard-stat">
            <div className="dashboard-stat-value">
              {dataLoading ? <span style={{ display: 'inline-block', width: 40, height: 20, borderRadius: 4, background: '#e5e7eb', animation: 'pulse 1.5s ease-in-out infinite' }} /> :pendingCount}
            </div>
            <span className="dashboard-stat-label">Pending signatures</span>
            <span className="dashboard-stat-sub">
              Waiting on clients or teammates to sign.
            </span>
          </div>
          <div className="dashboard-stat">
            <div className="dashboard-stat-value">
              {dataLoading ? <span style={{ display: 'inline-block', width: 40, height: 20, borderRadius: 4, background: '#e5e7eb', animation: 'pulse 1.5s ease-in-out infinite' }} /> :completedCount}
            </div>
            <span className="dashboard-stat-label">Completed</span>
            <span className="dashboard-stat-sub">
              Fully signed and finalized documents.
            </span>
          </div>
        </div>

        {/* PRIMARY ACTIONS */}
        <div className="dashboard-actions">
          <button
            className="primary-button"
            type="button"
            onClick={() => navigate("/send")}
          >
            Send document for signature
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={() => navigate("/templates")}
          >
            View templates
          </button>
        </div>

        {/* DROPZONE / UPLOAD CTA */}
        <div
          className={`dashboard-upload${dragOver ? " dashboard-upload--drag" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFileSelected(file);
          }}
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: "pointer" }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelected(file);
              e.target.value = "";
            }}
          />
          <div className="dashboard-upload-inner">
            <div className="dashboard-upload-icon">&#11014;</div>
            <div className="dashboard-upload-main">
              <div className="dashboard-upload-title">
                Upload a PDF to start
              </div>
              <div className="dashboard-upload-sub">
                Drag &amp; drop, or click to browse from your computer.
              </div>
            </div>
            <button
              type="button"
              className="dashboard-upload-button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse files
            </button>
          </div>
        </div>

        {/* DOCUMENTS TABLE CARD */}
        <div className="dashboard-documents">
          <div className="dashboard-documents-header">
            <h3>My documents</h3>
            <span className="dashboard-documents-count">
              {dataLoading ? "..." : `${totalDocs} total`}
            </span>
          </div>

          {/* Loading spinner */}
          {dataLoading && (
            <div className="documents-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "48px 20px",
                  gap: 10,
                  color: "#6b7280",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    border: "2.5px solid #e5e7eb",
                    borderTopColor: "#0f172a",
                    borderRadius: "50%",
                    animation: "ds-spin 0.7s linear infinite",
                  }}
                />
                Loading documents...
              </div>
            </div>
          )}

          {/* Empty state */}
          {!dataLoading && contracts.length === 0 && (
            <EmptyState
              icon={<DocumentsIllustration />}
              title="No documents yet"
              subtitle="Send your first document for signature and it will appear here"
              actionLabel="Send a Document"
              onAction={() => navigate("/send")}
              preview={ghostPreview}
            />
          )}

          {/* Real data table */}
          {!dataLoading && contracts.length > 0 && (
            <div className="documents-card">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Date sent</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr
                      key={c.contractURL}
                      onClick={() =>
                        navigate(`/document-status/${c.contractURL}`)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>
                        {c.contractName}
                      </td>
                      <td>
                        {c.recipients?.length ?? 0} recipient
                        {(c.recipients?.length ?? 0) === 1 ? "" : "s"}
                      </td>
                      <td>
                        <span
                          className={`status-pill ${getStatusPillClass(c.computedStatus)}`}
                        >
                          {c.computedStatus}
                        </span>
                      </td>
                      <td>{formatDate(c.dateSent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalDocs > 5 && (
                <div
                  style={{
                    padding: "10px 14px",
                    borderTop: "1px solid #f1f5f9",
                    textAlign: "center",
                  }}
                >
                  <button
                    type="button"
                    className="link-button"
                    style={{ fontSize: 12.5 }}
                    onClick={() => navigate("/document-status")}
                  >
                    View all {totalDocs} documents
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT SIDEPANEL */}
      <div className="dashboard-sidepanel">
        <div className="activity-section">
          <h4>Recent activity</h4>
          {dataLoading && (
            <p style={{ fontSize: 11.5, color: "#6b7280" }}>Loading...</p>
          )}
          {!dataLoading && contracts.length === 0 && (
            <p style={{ fontSize: 11.5, color: "#6b7280" }}>
              No recent activity yet. Send a document to get started.
            </p>
          )}
          {!dataLoading && contracts.length > 0 && (
            <ul>
              {contracts.slice(0, 3).map((c) => {
                const isCompleted =
                  c.computedStatus.toLowerCase() === "completed";
                const isSent = c.computedStatus.toLowerCase() === "sent";
                return (
                  <li key={c.contractURL}>
                    <span
                      className={`activity-dot ${
                        isCompleted
                          ? "activity-dot--signed"
                          : isSent
                          ? "activity-dot--sent"
                          : "activity-dot--reminder"
                      }`}
                    />
                    <div className="activity-main">
                      <span className="activity-title">
                        <strong>{c.contractName}</strong>
                      </span>
                      <span className="activity-meta">
                        {formatDate(c.lastActivityAt || c.dateSent)} &middot;{" "}
                        {c.computedStatus}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shortcuts-section">
          <h4>Shortcuts</h4>
          <ul>
            <li onClick={() => navigate("/document-status")}>
              Document status
            </li>
            <li onClick={() => navigate("/templates")}>Saved templates</li>
            <li onClick={() => navigate("/send")}>Send a document</li>
            <li onClick={() => navigate("/profile")}>Account settings</li>
          </ul>
        </div>

        <div className="onboarding-guide">
          Need help getting started?{" "}
          <a href="/send">Send your first document</a>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
